import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    filter,
    map,
    merge,
    mergeMap,
    of,
    withLatestFrom,
} from "rxjs";
import { SocketTransportService } from "../../../core/realtime/socket-transport.service";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";
import { SessionActions } from "../../authentication/store/session.actions";
import { sessionFeature } from "../../authentication/store/session.reducer";
import { ActionsApiService } from "../data-access/actions-api.service";
import {
    ActionLoadErrorCode,
    ActionSocketMessage,
} from "../models/action.models";
import { TaskActions } from "./actions.actions";
import { actionsFeature, ACTIONS_PAGE_SIZE } from "./actions.reducer";

@Injectable()
export class TaskActionEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(ActionsApiService);
    private readonly socket = inject(SocketTransportService);

    readonly initialLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.currentUserLoaded),
            map(() =>
                TaskActions.loadRequested({
                    collection: "active",
                    latestId: null,
                    reset: true,
                    advance: false,
                })
            )
        )
    );

    readonly loadOnOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskActions.pageOpened),
            withLatestFrom(this.store.select(actionsFeature.selectActive)),
            filter(([, active]) => !active.loading),
            map(() =>
                TaskActions.loadRequested({
                    collection: "active",
                    latestId: null,
                    reset: true,
                    advance: false,
                })
            )
        )
    );

    readonly loadArchivedOnOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskActions.archivedOpened),
            withLatestFrom(this.store.select(actionsFeature.selectArchived)),
            filter(([, archived]) => !archived.loaded && !archived.loading),
            map(() =>
                TaskActions.loadRequested({
                    collection: "archived",
                    latestId: null,
                    reset: true,
                    advance: false,
                })
            )
        )
    );

    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskActions.loadRequested),
            mergeMap(({ collection, latestId, reset, advance }) =>
                this.api.getPage(collection === "archived", latestId).pipe(
                    withSkeletonTiming(),
                    map((page) =>
                        TaskActions.loadSucceeded({
                            collection,
                            actions: page.actions,
                            latestId: page.latestId,
                            reset,
                            advance,
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            TaskActions.loadFailed({
                                collection,
                                error: this.errorCode(error),
                            })
                        )
                    )
                )
            )
        )
    );

    readonly next$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskActions.nextRequested),
            withLatestFrom(
                this.store.select(actionsFeature.selectTaskActionsState)
            ),
            map(([{ collection }, state]) => ({
                collection,
                current: state[collection],
            })),
            filter(({ current }) => !current.loading),
            map(({ collection, current }) => {
                const nextStart = (current.pageIndex + 1) * ACTIONS_PAGE_SIZE;
                if (nextStart < current.items.length) {
                    return TaskActions.pageChanged({
                        collection,
                        pageIndex: current.pageIndex + 1,
                    });
                }
                return TaskActions.loadRequested({
                    collection,
                    latestId: current.latestId,
                    reset: false,
                    advance: true,
                });
            })
        )
    );

    readonly previous$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TaskActions.previousRequested),
            withLatestFrom(
                this.store.select(actionsFeature.selectTaskActionsState)
            ),
            map(([{ collection }, state]) =>
                TaskActions.pageChanged({
                    collection,
                    pageIndex: state[collection].pageIndex - 1,
                })
            )
        )
    );

    readonly socketActions$ = createEffect(() =>
        merge(
            this.socket
                .on<ActionSocketMessage>("action_assigned")
                .pipe(
                    map((message) => ({ kind: "assigned" as const, message }))
                ),
            this.socket
                .on<ActionSocketMessage>("action_status")
                .pipe(map((message) => ({ kind: "status" as const, message })))
        ).pipe(
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            filter(
                ([{ message }, user]) =>
                    Boolean(user) &&
                    message.usersToNotify?.includes(user!.username)
            ),
            map(([event]) =>
                event.kind === "assigned"
                    ? TaskActions.socketAssigned({ message: event.message })
                    : TaskActions.socketStatusUpdated({
                          message: event.message,
                      })
            )
        )
    );

    readonly reset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.logoutCompleted, SessionActions.loginReset),
            map(() => TaskActions.reset())
        )
    );

    private errorCode(error: unknown): ActionLoadErrorCode {
        if (!(error instanceof HttpErrorResponse)) return "unknown";
        if (error.status === 0) return "network";
        if (error.status === 401 || error.status === 403) return "unauthorized";
        if (error.status === 429) return "rateLimited";
        if (error.status >= 500) return "server";
        return "unknown";
    }
}
